import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { PVMUrls } from 'config/urls';
import _ from 'lodash';
import { getMinAndMaxValues, processGroupData } from 'parser/pvmAnalysisParser';

const errorMessage: string = 'Something went wrong!';

interface Props {
    queryKey: any[];
    variables?: any;
    queryConfig?: {
        enabled?: boolean;
    };
}

interface outputType {
    data: any;
    loading: boolean;
    error: any;
}

function useFetchFeatureData({ queryKey, variables = {}, queryConfig = {} }: Props): outputType {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<any | null>(null);

    // Queries
    const {
        data: apiData,
        isFetching,
        isSuccess,
        fetchStatus,
        error: apiError,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            //initializing states
            setData(null);
            setError(null);
            setLoading(true);

            try {
                const data = await axios({
                    method: 'post',
                    url: PVMUrls.FeatureDataUrl,
                    data: { ...variables },
                });
                return data;
            } catch (error) {
                onFetchError(error);
            }
        },
        ...queryConfig,
        //  enabled: false,
    });

    const apiLoading = isFetching || fetchStatus === 'paused';
    const apiSuccess = !apiLoading && !apiError && isSuccess;

    //error handling
    if (!apiLoading && apiError && !error) {
        // if (onError) return onError(apiError);
        onFetchError(apiError);
    }
    useEffect(() => {
        //success handling
        if (apiSuccess && apiData?.data) {
            if (error) setError(null);
            // if (onComplete) return onComplete(apiData?.data);
            onFetchComplete(apiData.data);
        }
    }, [apiData?.data, apiSuccess]);

    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            const parsedData: any = { ...getMinAndMaxValues(payload) };
            parsedData.data = processGroupData(payload, false);
            setData(parsedData);
        } catch (error) {
            onFetchError(error);
            console.error(errorMessage, error);
        } finally {
            setLoading(false);
        }
    }

    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        setData(null);
        setLoading(false);
        setError(error);
    }
    const outputData: outputType = {
        loading,
        error,
        data,
    };
    return outputData;
}

export { useFetchFeatureData };
